import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { DefaultSortTypesEnum, OptionKey, PaginationParams } from '@/common/types';
import { API_URL } from '@/dtos';
import { IInvoiceDashboardAggregatorDto, IInvoiceDashboardPageDto, IInvoiceStatusType, IInvoiceType } from '@/types';

import { InvoiceTypeView } from '../types/invoicing.constants';
import { Invoice } from '../types/invoicing.types';

export interface InvoicesQueryParams extends Partial<PaginationParams> {
  types?: OptionKey[];
  statuses?: OptionKey[];
  methods?: OptionKey[];
  dateRange?: Array<string | null>;
  sortBy?: DefaultSortTypesEnum;
}

const DEFAULT_INVOICE_TYPES = [
  IInvoiceType.ORGANIZATION_LEVEL_PREFUND_REPLENISHMENT,
  IInvoiceType.PLAN_LEVEL_PREFUND_REPLENISHMENT,
  IInvoiceType.ORGANIZATION_LEVEL_CONTRIBUTIONS,
  IInvoiceType.PLAN_LEVEL_REPLENISHMENT,
  IInvoiceType.REPLENISHMENT,
  IInvoiceType.PLAN_LEVEL_PREFUND_REPLENISHMENT_RETURN,
  IInvoiceType.ORGANIZATION_LEVEL_PREFUND_REPLENISHMENT_RETURN,
  IInvoiceType.PLAN_LEVEL_CONTRIBUTIONS,
  IInvoiceType.EOY_PLAN_RECONCILIATION,
  IInvoiceType.BALANCE_ADJUSTMENT,
];

const DEFAULT_INVOICE_STATUSES = [
  IInvoiceStatusType.PAYMENT_INITIATED,
  IInvoiceStatusType.PAYMENT_REQUESTED,
  IInvoiceStatusType.PAYMENT_REJECTED,
  IInvoiceStatusType.PAID,
  IInvoiceStatusType.PAYMENT_FAILED,
  IInvoiceStatusType.REOPEN,
  IInvoiceStatusType.ACTION_REQUIRED,
  IInvoiceStatusType.CANCELED,
];

interface ResponseWithPagination extends IInvoiceDashboardPageDto {
  totalElements?: number;
}

const useInvoicesQuery = ({
  page,
  perPage,
  searchString,
  dateRange,
  statuses,
  types,
  methods,
  sortBy,
}: InvoicesQueryParams) => {
  const [endedFrom, endedTo] = dateRange || [];
  const invoiceTypes = types?.length ? types : DEFAULT_INVOICE_TYPES;
  const invoiceStatuses = statuses?.length ? statuses : DEFAULT_INVOICE_STATUSES;

  const params = {
    page,
    size: perPage || 50,
    ended_from: endedFrom,
    ended_to: endedTo,
    ...(sortBy ? { sort: `invoiceStatuses.validFrom,${sortBy}` } : {}),
    ...(searchString ? { search_value: searchString } : {}),
    invoice_status_types: invoiceStatuses.map((status) => `${status || ''}`?.toUpperCase()).join(','),
    invoice_types: invoiceTypes.map((type) => `${type || ''}`?.toUpperCase()).join(','),
    ...(methods && methods.length
      ? { ach_methods: methods.map((method) => `${method || ''}`?.toUpperCase()).join(',') }
      : {}),
  };

  const { data, isLoading } = useQuery(
    [
      API_URL.INVOICES_DASHBOARD,
      page,
      perPage,
      searchString,
      sortBy,
      endedFrom,
      endedTo,
      invoiceStatuses,
      invoiceTypes,
      methods,
    ],
    () => api.get<ResponseWithPagination>(API_URL.INVOICES_DASHBOARD, { params })
  );

  const formattedData = useMemo(
    () =>
      data?.data?.content?.map(
        (invoice: IInvoiceDashboardAggregatorDto): Invoice => ({
          amount: invoice.amount,
          endedAt: invoice.ended_at ? `${invoice.ended_at}` : undefined,
          invoiceId: toString(invoice.invoice_id),
          invoiceStatusType: invoice.invoice_status_type,
          invoiceType: invoice?.invoice_type
            ? (InvoiceTypeView[invoice.invoice_type as IInvoiceType] ?? '-')
            : '-',
          largeOrganizationLogo: invoice.large_organization_logo,
          organizationName: invoice.organization_name,
        })
      ),
    [data]
  );

  const total = useMemo(() => data?.data.totalElements || 0, [data?.data]);

  return {
    formattedData: formattedData || [],
    isLoading,
    total,
  };
};

export default useInvoicesQuery;
